import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MODULE_KEYS, MODULE_LABELS, type ModuleKey } from "@gaole/shared";
import { api } from "../lib/api";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { PageLoader, SettingsCard, Toggle } from "../components/ui";

export function Modules() {
  const queryClient = useQueryClient();
  const bootstrap = useAuth((s) => s.bootstrap);
  const { can } = useAuth();
  const canSettings = can("settings.write");

  const { data: modules, isLoading } = useQuery({
    queryKey: ["rbac", "modules"],
    queryFn: () => api<{ modules: Record<ModuleKey, boolean> }>("/rbac/modules").then((r) => r.modules),
  });

  const { data: runtimeSettings } = useQuery({
    queryKey: ["settings", "module-runtime"],
    queryFn: () =>
      api<{ settings: { key: string; value: unknown }[] }>("/settings").then((r) => {
        const map = new Map(r.settings.map((s) => [s.key, s.value]));
        return {
          marketplaceEnabled: Boolean(map.get("marketplace.enabled")),
          flexxiEnabled: Boolean(map.get("flexxi.enabled")),
        };
      }),
    enabled: Boolean(modules && canSettings),
  });

  const saveRuntimeMut = useMutation({
    mutationFn: (values: Record<string, boolean>) =>
      api("/settings", { method: "PUT", body: { values } }),
    onSuccess: () => {
      toast("success", "Saved");
      void queryClient.invalidateQueries({ queryKey: ["settings", "module-runtime"] });
      void bootstrap();
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const toggleMut = useMutation({
    mutationFn: ({ key, enabled }: { key: ModuleKey; enabled: boolean }) =>
      api(`/rbac/modules/${key}`, { method: "PUT", body: { enabled } }),
    onMutate: async ({ key, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ["rbac", "modules"] });
      const prev = queryClient.getQueryData<Record<ModuleKey, boolean>>(["rbac", "modules"]);
      queryClient.setQueryData<Record<ModuleKey, boolean>>(["rbac", "modules"], (old) =>
        old ? { ...old, [key]: enabled } : old,
      );
      return { prev };
    },
    onError: (e: Error, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["rbac", "modules"], ctx.prev);
      toast("error", e.message);
    },
    onSuccess: async () => {
      toast("success", "Module updated (runtime API setting synced when applicable)");
      await queryClient.invalidateQueries({ queryKey: ["rbac", "modules"] });
      await queryClient.invalidateQueries({ queryKey: ["settings", "module-runtime"] });
      await bootstrap();
    },
  });

  if (isLoading || !modules) return <PageLoader />;

  const marketplaceModuleOn = modules.marketplace !== false;
  const flexxiModuleOn = modules.instalments !== false;
  const marketplaceRuntimeOn = runtimeSettings?.marketplaceEnabled ?? false;
  const flexxiRuntimeOn = runtimeSettings?.flexxiEnabled ?? false;

  return (
    <div className="page-scroll w-full min-w-0 space-y-5">
      <div>
        <h1 className="font-display text-lg font-bold">Feature modules</h1>
        <p className="mt-0.5 text-xs text-white/50">
          Enable or disable platform capabilities store-wide. Disabled modules hide routes and APIs for all users.
        </p>
      </div>

      <SettingsCard
        title="Modules"
        description="Changes take effect immediately for new requests. Staff may need to refresh their session."
      >
        <div className="divide-y divide-line">
          {MODULE_KEYS.map((key) => (
            <div key={key} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
              <div>
                <p className="text-sm font-medium">{MODULE_LABELS[key]}</p>
                <p className="font-mono text-[10px] text-white/35">{key}</p>
              </div>
              <Toggle
                checked={modules[key] !== false}
                disabled={toggleMut.isPending}
                onChange={(enabled) => toggleMut.mutate({ key, enabled })}
              />
            </div>
          ))}
        </div>
      </SettingsCard>

      {canSettings && (marketplaceModuleOn || flexxiModuleOn) ? (
        <SettingsCard
          title="Module runtime switches"
          description="Turn public APIs on or off (seller portal, FlexxiPlan). Module toggles above sync these when the API is updated."
        >
          <p className="mb-4 text-xs text-muted">
            Full configuration:{" "}
            {marketplaceModuleOn ? (
              <Link className="text-brand-pink hover:underline" to="/marketplace/settings">
                Marketplace settings
              </Link>
            ) : null}
            {marketplaceModuleOn && flexxiModuleOn ? " · " : null}
            {flexxiModuleOn ? (
              <Link className="text-brand-pink hover:underline" to="/flexxi/settings">
                FlexxiPlan settings
              </Link>
            ) : null}
          </p>
          <div className="divide-y divide-line">
            {marketplaceModuleOn ? (
              <div className="flex items-center justify-between gap-4 py-3 first:pt-0">
                <div>
                  <p className="text-sm font-medium">Marketplace API live</p>
                  <p className="text-[11px] text-muted">
                    Setting <span className="font-mono">marketplace.enabled</span> — seller apply & listings
                  </p>
                  {!marketplaceRuntimeOn ? (
                    <p className="mt-1 text-[11px] text-amber-400/90">
                      Module is on but API still returns “Marketplace is not enabled” until this is on.
                    </p>
                  ) : null}
                </div>
                <Toggle
                  checked={marketplaceRuntimeOn}
                  disabled={saveRuntimeMut.isPending}
                  onChange={(enabled) => saveRuntimeMut.mutate({ "marketplace.enabled": enabled })}
                />
              </div>
            ) : null}
            {flexxiModuleOn ? (
              <div className="flex items-center justify-between gap-4 py-3 last:pb-0">
                <div>
                  <p className="text-sm font-medium">FlexxiPlan API live</p>
                  <p className="text-[11px] text-muted">
                    Setting <span className="font-mono">flexxi.enabled</span> — requires Vault module too
                  </p>
                </div>
                <Toggle
                  checked={flexxiRuntimeOn}
                  disabled={saveRuntimeMut.isPending}
                  onChange={(enabled) => saveRuntimeMut.mutate({ "flexxi.enabled": enabled })}
                />
              </div>
            ) : null}
          </div>
        </SettingsCard>
      ) : null}
    </div>
  );
}
